import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createGym, getAllGyms } from '../services/firebase/gym';
import { Gym } from '../models/gym/Gym';
import { useAuth } from '../contexts/AuthContext';
import '../styles/AddGym.css';

const AddGym: React.FC = () => {
  const [gymName, setGymName] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [styles, setStyles] = useState<string[]>(['Bouldering', 'Rope climbing']); // Both checked by default
  
  // Available gym styles
  const availableStyles = ['Bouldering', 'Rope climbing'];
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [noGymsExist, setNoGymsExist] = useState(false);
  const navigate = useNavigate();
  const { currentUser, climberProfile } = useAuth();
  
  useEffect(() => {
    // Check if there are any gyms in the system
    const checkGyms = async () => {
      try {
        const gyms = await getAllGyms();
        setNoGymsExist(gyms.length === 0);
        console.log('No gyms exist:', gyms.length === 0);
      } catch (error) {
        console.error('Error checking for gyms:', error);
      }
    };
    
    checkGyms();
  }, []);
  
  useEffect(() => {
    // Check if the user has the 'fre' role in any of their gyms
    const checkAuthorization = () => {
      if (!currentUser || !climberProfile) {
        setIsAuthorized(false);
        setError('You must be logged in to create a gym');
        return;
      }
      
      // Check if user has 'fre' role in any gym
      const hasFreRole = climberProfile.gymMemberships ? 
        Object.values(climberProfile.gymMemberships).some(role => role === 'fre') : 
        false;
      
      setIsAuthorized(hasFreRole);
      
      if (!hasFreRole && !noGymsExist) {
        setError('Only users with the "fre" role in any gym can create new gyms');
      }
    };
    
    checkAuthorization();
  }, [currentUser, climberProfile, noGymsExist]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isAuthorized && !noGymsExist) {
      return setError('You are not authorized to create gyms');
    }
    
    if (!gymName.trim()) {
      return setError('Gym name is required');
    }

    try {
      setLoading(true);
      setError('');
      
      // Create a unique ID for the gym (timestamp + random, without special chars)
      const timestamp = Date.now();
      const randomString = Math.random().toString(36).substring(2, 9);
      const gymId = `gym_${timestamp}_${randomString}`;
      
      // Create the new gym object
      const newGym = new Gym(gymId, {
        name: gymName.trim(),
        imageUrl: imageUrl.trim() || undefined,
        logoUrl: logoUrl.trim() || undefined,
        styles: styles
      });
      
      // Save to Firestore
      await createGym(newGym);
      
      // Navigate back to gyms list
      navigate('/gyms');
    } catch (err: any) {
      console.error('Error saving gym:', err);
      setError('Failed to save gym. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-gym-container">
      <div className="add-gym-header">
        <h1>Add New Gym</h1>
        <button 
          className="back-button"
          onClick={() => navigate('/gyms')}
        >
          ← Back
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <form className="add-gym-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="gymName">Gym Name *</label>
          <input
            type="text"
            id="gymName"
            value={gymName}
            onChange={(e) => setGymName(e.target.value)}
            placeholder="Enter gym name"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="imageUrl">Image URL</label>
          <input
            type="url"
            id="imageUrl"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="https://example.com/image.jpg (optional)"
          />
          {imageUrl && (
            <div className="image-preview">
              <img 
                src={imageUrl} 
                alt="Gym preview" 
                onError={(e) => {
                  e.currentTarget.src = 'https://via.placeholder.com/150?text=Invalid+URL';
                }}
              />
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="logoUrl">Logo URL</label>
          <input
            type="url"
            id="logoUrl"
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            placeholder="https://example.com/logo.jpg (optional)"
          />
          {logoUrl && (
            <div className="logo-preview">
              <img 
                src={logoUrl} 
                alt="Logo preview" 
                onError={(e) => {
                  e.currentTarget.src = 'https://via.placeholder.com/100?text=Invalid+URL';
                }}
              />
            </div>
          )}
        </div>

        <div className="form-group">
          <label>Gym Styles</label>
          <div className="styles-checkboxes">
            {availableStyles.map((style) => (
              <div key={style} className="style-checkbox">
                <input
                  type="checkbox"
                  id={`style-${style}`}
                  checked={styles.includes(style)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setStyles([...styles, style]);
                    } else {
                      setStyles(styles.filter(s => s !== style));
                    }
                  }}
                />
                <label htmlFor={`style-${style}`}>{style}</label>
              </div>
            ))}
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            className="cancel-button"
            onClick={() => navigate('/gyms')}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="save-button"
            disabled={loading || (!isAuthorized && !noGymsExist)}
          >
            {loading ? 'Saving...' : 'Save Gym'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddGym;
